import React, { useEffect, PropsWithChildren, useCallback } from 'react'
import * as Sentry from '@sentry/browser'
import { IUserObject } from 'types'
import {
  setMasqueradeToken,
  clearMasqueradeToken,
  getMasqueradeToken,
  getCurrentUser,
  markLoggedOut,
  clearLoggedOutFlag,
  hasBeenLoggedOut
} from 'helpers/auth'
import { safeLocalStorage } from 'helpers/localStorage'
import api, { NewUserParams } from 'helpers/api'
import { validateSourceUrl } from 'helpers/utils'
import { useNavigate } from 'react-router-dom'
import { SIGN_IN } from 'routes'
import { AUTH_KEY, FREE_TRIAL_BANNER_DISMISSED_KEY } from 'app-constants'
import { AuthContext, AuthStore } from 'hooks/auth'
import { hydrateThemeFromProfile, setThemeOwner } from 'hooks/useTheme'

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  'use no memo'
  const navigate = useNavigate()
  // Initialize user from localStorage on mount
  const [user, setUser] = React.useState<IUserObject | undefined>(() => {
    const restored = getCurrentUser()
    // Not just updateUser: the theme owner has to be right from the first
    // render, or an in-flight write could be folded into the wrong account.
    setThemeOwner(restored?.publicId)
    return restored
  })

  // Takes a partial so a caller can change one field (favourites, say) without
  // replaying a snapshot it read before an await over newer state.
  const updateUser = useCallback(async (info: Partial<IUserObject>) => {
    // If the session was forcibly ended (401 handler or logout) while this
    // call was in flight, do not resurrect it by writing AUTH_KEY back.
    if (hasBeenLoggedOut()) return

    setUser((prev) => {
      if (hasBeenLoggedOut()) return prev
      const merged = {
        ...prev, // always merge with latest prev
        ...info,
        authToken: info.authToken ?? prev?.authToken,
        authTokenExpiresAt: info.authTokenExpiresAt ?? prev?.authTokenExpiresAt,
        favourites: info.favourites ?? prev?.favourites ?? [],
        isSuperAdmin: info.isSuperAdmin ?? prev?.isSuperAdmin,
        // Validate a picture that arrives; an update that leaves it out keeps
        // the current one. A null from the server still clears it.
        profilePicture:
          'profilePicture' in info
            ? validateSourceUrl(info.profilePicture)
            : (prev?.profilePicture ?? validateSourceUrl()),
        integrations: info.integrations ?? prev?.integrations ?? []
      } as IUserObject
      safeLocalStorage.setItem(AUTH_KEY, JSON.stringify(merged))
      // The profile is the record for appearance; this browser's cache is only
      // what the pre-paint script had to guess with. Reconcile on every arrival
      // of a user object — sign-in, /user/me, token refresh.
      setThemeOwner(merged.publicId)
      hydrateThemeFromProfile(merged.theme)
      if (merged?.email) Sentry.setUser({ email: merged.email })
      return merged
    })

    // Yield a microtask so consumers can re-render before navigation
    await Promise.resolve()
  }, [])

  const login = useCallback(
    async (emailAddress: string, password: string) => {
      // Clear any stale masquerade token before issuing the login request so
      // the new session isn't routed through BearerMasquerade (which the
      // Authorization header otherwise prefers over the user's Bearer token).
      clearMasqueradeToken()

      const response = await api.loginUser(emailAddress, password)

      if (response.metadata.requireSSORedirect && response.metadata.ssoRedirectURL) {
        window.location.href = response.metadata.ssoRedirectURL
        return
      }

      // Authentication succeeded — only now is it safe to release the latch so
      // updateUser can write the new session into AUTH_KEY.
      clearLoggedOutFlag()
      await updateUser(response.data)
    },
    [updateUser]
  )

  const register = useCallback(async (userData: NewUserParams) => {
    clearMasqueradeToken()

    const response = await api.createUser(userData)

    if (response.metadata.requireSSORedirect && response.metadata.ssoRedirectURL) {
      window.location.href = response.metadata.ssoRedirectURL
      return
    }
  }, [])

  const logout = async (serverSideLogout = true) => {
    const wasMasquerading = !!getMasqueradeToken()

    // Latch logged-out state before any awaits so a concurrent updateUser
    // (from an in-flight API response) cannot rewrite AUTH_KEY mid-logout.
    markLoggedOut()
    // Clear masquerade token first so no in-flight request can use it after this point
    clearMasqueradeToken()

    if (!wasMasquerading && serverSideLogout) {
      try {
        await api.logoutUser()
        navigate(SIGN_IN)
      } catch {
        navigate(SIGN_IN)
      }
    }

    // Clear local storage *before* clearing state
    safeLocalStorage.removeItem(AUTH_KEY)
    safeLocalStorage.removeItem(FREE_TRIAL_BANNER_DISMISSED_KEY)

    Sentry.setUser(null)
    setThemeOwner(undefined)
    setUser(undefined)
    navigate(SIGN_IN)
  }

  const startGoogleAuth = useCallback(async (template?: string) => {
    try {
      const response = await api.getGoogleOauthConnectUrl(template)
      window.location.href = response.data.url
    } catch {
      /* no-op */
    }
  }, [])

  const startSSOAuth = useCallback(async (email: string) => {
    try {
      const response = await api.getSSOConnectUrl(email)
      window.location.href = response.data.ssoRedirectURL
    } catch {
      /* no-op */
    }
  }, [])

  const startMicrosoftAuth = useCallback(async (template?: string) => {
    const response = await api.getMicrosoftOauthConnectUrl(template)
    window.location.href = response.data.url
  }, [])

  const startMasquerade = useCallback(
    async (token: string) => {
      setMasqueradeToken(token)
      const response = await api.getMe()
      await updateUser({
        ...response.data,
        authTokenExpiresAt: new Date(Date.now() + 3600000).toISOString()
      } as IUserObject)
    },
    [updateUser]
  )

  const refreshUser = useCallback(async () => {
    const response = await api.getMe()
    await updateUser(response.data as IUserObject)
  }, [updateUser])

  // Listen for storage events to sync token updates across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === AUTH_KEY) {
        const currentUserFromStorage = getCurrentUser()

        if (currentUserFromStorage) {
          setUser((prev) => {
            // Only update when something this tab acts on changed, to avoid needless
            // re-renders: the token's expiry, the account, or its appearance. A
            // theme change in another tab re-saves the same session with the same
            // expiry, so checking the expiry alone missed it.
            const changed =
              prev?.authTokenExpiresAt !== currentUserFromStorage.authTokenExpiresAt ||
              prev?.publicId !== currentUserFromStorage.publicId ||
              prev?.theme !== currentUserFromStorage.theme
            if (changed) {
              if (currentUserFromStorage?.email) Sentry.setUser({ email: currentUserFromStorage.email })
              setThemeOwner(currentUserFromStorage.publicId)
              // Another tab may have signed in as someone else; take on that
              // account's appearance too, as updateUser does. Hydrating only
              // applies it here, it never writes back.
              hydrateThemeFromProfile(currentUserFromStorage.theme)
              return currentUserFromStorage
            }
            return prev
          })
        } else {
          // Token was removed (logout) - localStorage is already cleared by the browser
          setThemeOwner(undefined)
          setUser(undefined)
          Sentry.setUser(null)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  const onPaidPlan = !!user && (user as any).subscriptionLevel === 1
  const isTeamManager = user?.isTeamManager ?? false

  const value: AuthStore = {
    user,
    onPaidPlan,
    isTeamManager,
    logout,
    login,
    register,
    updateUser,
    startGoogleAuth,
    startMicrosoftAuth,
    startSSOAuth,
    startMasquerade,
    refreshUser
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
